import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Wallet, TrendingUp, ArrowUpRight, ArrowDownRight } from "lucide-react";
import TransactionPieChart from "./TransactionPieChart";
import TransactionBarChart from "./TransactionBarChart";
import { useUser } from "../contexts/UserContext"; // Add this import
import YearlyReport from "./YearlyReport";

const Analytics = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [transactions, setTransactions] = useState([]);
  const { user } = useUser(); // Add user context

  // Fetch apps and transactions
  const fetchTransaction = async () => {
    if (!user?.id) return; // Don't fetch if there's no user

    try {
      const response = await fetch(`${API_BASE_URL}transactions/${user.id}`);

      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  useEffect(() => {
    fetchTransaction();
  }, [user]);

  // Transform transactions for the timeline chart
  const transactionTimelineData = useMemo(() => {
    let runningBalance = 0;
    return transactions
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((transaction) => {
        const amount = Math.abs(transaction.amount);
        if (transaction.type === "deposit") {
          runningBalance -= amount;
        } else {
          runningBalance += amount;
        }
        return {
          date: new Date(transaction.date).toLocaleDateString("en-US", {
            timeZone: "UTC",
          }),
          balance: runningBalance,
          amount: transaction.type === "deposit" ? -amount : amount,
          type: transaction.type,
        };
      });
  }, [transactions]);

  // Calculate monthly data for other charts
  const monthlyData = useMemo(() => {
    const data = {};

    transactions.forEach((transaction) => {
      const date = new Date(transaction.date);
      const monthYear = date.toLocaleDateString("en-US", {
        timeZone: "UTC",
        month: "short",
        year: "2-digit",
      });

      if (!data[monthYear]) {
        data[monthYear] = {
          month: monthYear,
          deposits: 0,
          withdrawals: 0,
          netProfit: 0,
          transactionCount: 0,
        };
      }

      const amount = Math.abs(transaction.amount);
      if (transaction.type === "deposit") {
        data[monthYear].deposits += amount;
        data[monthYear].netProfit -= amount;
      } else {
        data[monthYear].withdrawals += amount;
        data[monthYear].netProfit += amount;
      }
      data[monthYear].transactionCount++;
    });

    return Object.values(data).sort(
      (a, b) => new Date(a.month) - new Date(b.month)
    );
  }, [transactions]);

  // Calculate overall metrics
  const metrics = useMemo(() => {
    const totalDeposits = transactions.reduce(
      (sum, t) => (t.type === "deposit" ? sum + Math.abs(t.amount) : sum),
      0
    );
    const totalWithdrawals = transactions.reduce(
      (sum, t) => (t.type === "withdraw" ? sum + Math.abs(t.amount) : sum),
      0
    );
    const netProfit = totalWithdrawals - totalDeposits;
    const profitMargin = totalDeposits ? (netProfit / totalDeposits) * 100 : 0;

    return {
      totalDeposits,
      totalWithdrawals,
      netProfit,
      profitMargin,
      totalTransactions: transactions.length,
    };
  }, [transactions]);

  // Custom tooltip for the transaction timeline
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border rounded shadow">
          <p className="font-medium">{label}</p>
          <p className="text-sm">Balance: ${payload[0].value.toFixed(2)}</p>
          {payload[1] && (
            <p className="text-sm">
              Transaction: ${Math.abs(payload[1].value).toFixed(2)}(
              {payload[1].payload.type})
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Add loading state for better UX
  if (!user?.id) {
    return <div className="p-4">Please log in to view analytics.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card style={{ backgroundColor: "#FFFFFF" }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Net Profit</p>
                <p
                  className={`text-2xl font-bold ${
                    metrics.netProfit >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  ${metrics.netProfit.toFixed(2)}
                </p>
              </div>
              <TrendingUp
                className={`h-8 w-8 ${
                  metrics.netProfit >= 0 ? "text-green-600" : "text-red-600"
                }`}
              />
            </div>
          </CardContent>
        </Card>

        <Card style={{ backgroundColor: "#FFFFFF" }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">ROI</p>
                <p
                  className={`text-2xl font-bold ${
                    metrics.profitMargin >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {metrics.profitMargin.toFixed(2)}%
                </p>
              </div>
              <Wallet
                className={`h-8 w-8 ${
                  metrics.profitMargin >= 0 ? "text-green-600" : "text-red-600"
                }`}
              />
            </div>
          </CardContent>
        </Card>

        <Card style={{ backgroundColor: "#FFFFFF" }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Total Deposits
                </p>
                <p className="text-2xl font-bold text-red-600">
                  ${metrics.totalDeposits.toFixed(2)}
                </p>
              </div>
              <ArrowDownRight className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card style={{ backgroundColor: "#FFFFFF" }}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Total Withdrawals
                </p>
                <p className="text-2xl font-bold text-green-600">
                  ${metrics.totalWithdrawals.toFixed(2)}
                </p>
              </div>
              <ArrowUpRight className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction Timeline Chart */}
      <Card style={{ backgroundColor: "#FFFFFF" }}>
        <CardHeader>
          <CardTitle>Transaction Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={transactionTimelineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  interval="preserveStartEnd"
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="balance"
                  stroke="#16a34a"
                  name="Balance"
                  strokeWidth={2}
                  dot={{ r: 1 }}
                />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#6366f1"
                  name="Transaction"
                  strokeWidth={1}
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <TransactionBarChart transactions={transactions} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Deposits vs Withdrawals Bar Chart */}
        <Card className="w-full" style={{ backgroundColor: "#FFFFFF" }}>
          <CardHeader>
            <CardTitle>Monthly Deposits vs Withdrawals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => `$${value.toFixed(2)}`}
                    labelStyle={{ color: "black" }}
                  />
                  <Legend />
                  <Bar dataKey="deposits" fill="#ef4444" name="Deposits" />
                  <Bar
                    dataKey="withdrawals"
                    fill="#16a34a"
                    name="Withdrawals"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <TransactionPieChart className="w-full" transactions={transactions} />
      </div>

      {/* Transaction Activity Chart
      <Card style={{ backgroundColor: '#FFFFFF' }}>
        <CardHeader>
          <CardTitle>Monthly Transaction Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="transactionCount"
                  stroke="#6366f1"
                  name="Transaction Count"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card> */}

      <YearlyReport transactions={transactions} />
    </div>
  );
};

export default Analytics;
